import * as yup from 'yup';

function requiredIfMarried<T extends yup.AnySchema>(
  schemaType: T,
  message: string,
  disableRequired = true,
): T {
  if (disableRequired) return schemaType.notRequired();

  return schemaType.when(['maritalStatus '], {
    is: (maritalStatus: string) => maritalStatus !== 'single',
    then: (schema: T) => schema.required(message),
    otherwise: (schema: T) => schema.notRequired(),
  });
}

function requiredIfPR<T extends yup.AnySchema>(
  schemaType: T,
  message: string,
  disableRequired = true,
): T {
  if (disableRequired) return schemaType.notRequired();

  return schemaType.when(['citizenship '], {
    is: (citizenship: string) =>
      citizenship === 'australian_pr_visa' ||
      citizenship === 'australian_tr_visa',
    then: (schema: T) => schema.required(message),
    otherwise: (schema: T) => schema.notRequired(),
  });
}

function requiredIfCondition<T extends yup.AnySchema>(
  schemaType: T,
  fields: string[],
  condition: (...values: any[]) => boolean,
  message: string,
  disableRequired = true,
): T {
  if (disableRequired) return schemaType.notRequired();

  return schemaType.when(fields, {
    is: (...fieldValues: any[]) => condition(...fieldValues),
    then: (schema: T) => schema.required(message),
    otherwise: (schema: T) => schema.notRequired(),
  });
}

export const personalDetailSchema = yup.object().shape({
  citizenship: yup.string().required('Please select any option.'),
  visaSubClass: requiredIfPR(yup.string(), 'Visa sub class is required.'),
  visaExpiryDate: requiredIfPR(yup.string(), 'Visa expire date is required.'),
  country: yup.string().required('Please select any option.'),
  permanentResident: yup.boolean().required('Please select any option.'),
  currentResidentalStatus: yup.string().required('Please select any option.'),
  residentalOtherSpecification: yup.string().when(['residentialStatus '], {
    is: (residentialStatus: string) => residentialStatus === 'other',
    then: (schema) => schema.required('Please provide more details'),
    otherwise: (schema) => schema.notRequired(),
  }),
  maritalStatus: yup.string().required('Please select any option.'),
  dependentChildren: requiredIfMarried(yup.boolean(), 'Please select option.'),
  numberOfDependents: requiredIfCondition(
    yup.number(),
    ['dependentChildren'],
    (dependentChildren: boolean) => dependentChildren === true,
    'Number of dependent children is required',
  ),
  rentExpense: requiredIfCondition(
    yup.number(),
    ['currentResidentalStatus'],
    (residentialStatus: string) => residentialStatus === 'renting',
    'Please enter your rent',
  ),
  frequencyOfRent: requiredIfCondition(
    yup.string(),
    ['currentResidentalStatus'],
    (residentialStatus: string) => residentialStatus === 'renting',
    'Please select your frequency of rent',
  ),
  isTheResidentialPlaceMortgaged: requiredIfCondition(
    yup.boolean(),
    ['currentResidentalStatus'],
    (residentialStatus: string) => residentialStatus === 'live_own_place',
    'Please select any option.',
  ),
});

export const personalDetailLabelMapper = {
  citizenship: 'What’s your citizenship?',
  country: 'Which country do you currently reside in?',
  permanentResident: 'Are you a Permanent Resident(PR) of this country?',
  currentResidentalStatus: 'What’s your current residential status?',
  maritalStatus: 'What is your marital status?',
  dependentChildren: 'Do you have any dependent children?',
};
